import React, { useEffect, useRef } from "react";
import Icon from "./Icon";

/**
 * Glass dialog with a blurred backdrop.
 *
 * Handles the accessibility basics a dialog needs: Escape to close, a click
 * on the backdrop to close, focus moved in on open and returned to the
 * trigger on close, Tab kept inside the panel, and the page behind locked
 * against scrolling.
 */
function Modal({ open, onClose, labelledBy, children, className = "" }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const restoreRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    restoreRef.current = document.activeElement;
    closeRef.current?.focus();

    const { overflow, paddingRight } = document.body.style;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      if (restoreRef.current instanceof HTMLElement) restoreRef.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div
        className="mw-backdrop absolute inset-0 bg-[#1E1B16]/50 backdrop-blur-md"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`mw-modal relative max-h-[92vh] w-full overflow-y-auto rounded-t-[1.75rem] border border-white/60 bg-white/85 shadow-[0_40px_90px_-30px_rgba(30,27,22,0.6)] backdrop-blur-2xl sm:max-w-2xl sm:rounded-[1.75rem] ${className}`}
      >
        <button
          ref={closeRef}
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 rounded-xl border border-[#EAE2D6] bg-white/80 p-2.5 text-[#4D4B46] backdrop-blur transition-colors duration-200 hover:border-[#D8B4FE] hover:text-[#6A0DAD] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20"
        >
          <Icon name="close" className="h-5 w-5" />
        </button>
        {children}
      </div>
    </div>
  );
}

export default Modal;
